/**
 * BarcodeScanner — a tablet- & phone-friendly camera overlay that resolves
 * a scanned 1D/2D barcode into a string. Built on `html5-qrcode`, which
 * works on iOS Safari (requires HTTPS) and Android Chrome with the
 * browser's MediaDevices API.
 *
 * Use:
 *   <BarcodeScanner
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onResult={(code) => handleCode(code)}
 *     title="Scan equipment sticker"
 *   />
 *
 * The component:
 *  - Picks a sensible camera (prefers the rear/environment lens)
 *  - Auto-stops the stream the moment a code is decoded so we don't keep
 *    the camera light on after the user is done.
 *  - Cleans up reliably on unmount even when the user closes the tab.
 */
import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { X, Camera, AlertTriangle, Keyboard } from "lucide-react";
import { Input } from "@/components/ui/input";

const REGION_ID = "haccp-barcode-region";

/** Subset of formats we actually print + scan. Restricting the decoder
 *  drops false-positive frames and roughly triples decode speed on iOS
 *  Safari's iPhone camera.
 *
 *  We deliberately keep this list SHORT — only the formats we actually
 *  encounter: QR on food-label stickers (contains verify URL + GS1),
 *  Code128 on equipment stickers, DataMatrix on supplier delivery
 *  cartons, EAN-13 on retail-packaged ingredients. Every extra format
 *  in this list slows every scan attempt (each frame is decoded once
 *  per format by ZXing/BarcodeDetector), which was the root cause of
 *  the "camera opens but doesn't detect" report — the decoder was
 *  timing out mid-frame trying 12 formats. */
const ACCEPTED_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,       // our own printed labels
  Html5QrcodeSupportedFormats.CODE_128,      // equipment stickers
  Html5QrcodeSupportedFormats.DATA_MATRIX,   // supplier deliveries
  Html5QrcodeSupportedFormats.EAN_13,        // retail ingredients
];

/** Heuristic: pick the rear/back camera from a deviceInfo list. iOS Safari
 *  labels usually contain "Back Camera"; Android Chrome uses "camera2 0,
 *  facing back". We prefer the LAST match so dual-camera devices favour
 *  the wide lens over the ultra-wide. */
function pickRearCamera(devices) {
  if (!devices || !devices.length) return null;
  const rear = devices.filter((d) =>
    /back|rear|environment|facing\s*back/i.test(d.label || "")
  );
  if (rear.length) return rear[rear.length - 1];
  return devices[devices.length - 1];
}

export default function BarcodeScanner({ open, onClose, onResult, title = "Scan barcode" }) {
  const scannerRef = useRef(null);
  const startedRef = useRef(false);
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");
  const [showManual, setShowManual] = useState(false);
  // Diagnostic hint shown if the camera opens but nothing is detected
  // for a while — helps staff differentiate "camera dead" (they see
  // the permission error) from "camera fine but I need to hold the
  // sticker closer / steadier".
  const [hint, setHint] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setHint("");
    setShowManual(false);
    let stopped = false;
    startedRef.current = false;
    const safeStop = async (s) => {
      if (!startedRef.current) return;
      try { await s.stop(); } catch { /* already stopped or never started */ }
      startedRef.current = false;
    };

    const decodeConfig = {
      fps: 12,
      // Square-ish scan window — the previous 2:1 rectangle (576×288)
      // cropped QR codes off-window on typical phone framing, which
      // was the top cause of the "label scan not detecting barcode"
      // report. A square box detects both QR and Code128 reliably; a
      // wide barcode fits horizontally while a QR fits without
      // vertical clipping.
      qrbox: (vw, vh) => {
        const min = Math.min(vw, vh);
        // Clamp to html5-qrcode's 50px minimum — on portrait phones the
        // video element briefly reports 0×0 before layout settles,
        // which would otherwise throw "Invalid config" (min size 50px)
        // and abort the start().
        const size = Math.max(50, Math.floor(min * 0.75));
        return { width: size, height: size };
      },
      aspectRatio: 1.0,
      // Constructor already restricted formats, but html5-qrcode
      // requires the same list on the start() config too or it
      // silently reverts to the full ZXing catalog (slow, misses).
      formatsToSupport: ACCEPTED_FORMATS,
      // Prefer the browser's native BarcodeDetector API on Chrome /
      // Edge / Android — faster and more accurate than ZXing. Safari
      // (both macOS and iOS) doesn't expose it, so we automatically
      // fall through to the ZXing decoder there — no separate branch.
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
    };

    const onDecoded = async (decoded) => {
      if (stopped) return;
      stopped = true;
      const scanner = scannerRef.current;
      if (scanner) {
        await safeStop(scanner);
        try { scanner.clear(); } catch { /* ignore */ }
      }
      onResult?.((decoded || "").trim());
    };

    const start = async () => {
      setError("");
      const scanner = new Html5Qrcode(REGION_ID, {
        verbose: false,
        formatsToSupport: ACCEPTED_FORMATS,
      });
      scannerRef.current = scanner;

      // iOS Safari is fussy about the camera constraint shape. Try three
      // strategies in order so we always end up with a working stream:
      //   1. Enumerate cameras and start the rear one by deviceId — most
      //      reliable on iPhones because the user-visible permission prompt
      //      fires first via getCameras() and the chosen camera is exact.
      //   2. Fall back to the loose `facingMode: "environment"` constraint.
      //   3. Last resort: `facingMode: "user"` so at least *something*
      //      renders if the device has no rear camera (e.g. a kiosk).
      const tryStart = async (constraint) => {
        await scanner.start(constraint, decodeConfig, onDecoded, () => {});
        startedRef.current = true;
      };

      try {
        const cams = await Html5Qrcode.getCameras();
        const rear = pickRearCamera(cams);
        if (rear) {
          await tryStart(rear.id);
          return;
        }
        throw new Error("No camera enumerated");
      } catch (e1) {
        // fall through to the facingMode path
      }
      try {
        await tryStart({ facingMode: "environment" });
        return;
      } catch (e2) {
        try {
          await tryStart({ facingMode: "user" });
          return;
        } catch (e3) {
          const msg = (e3 && (e3.message || e3.toString())) || "Camera unavailable";
          if (/permission|denied|NotAllowedError/i.test(msg)) {
            setError(
              "Camera permission denied. On iPhone: Settings → Safari → Camera → Allow. Or enter the code manually below."
            );
          } else if (/NotFoundError|no camera|enumerated/i.test(msg)) {
            setError("No camera was found. Enter the code manually below.");
          } else if (/secure context|https/i.test(msg)) {
            setError("Camera requires a secure (https://) connection.");
          } else if (/NotReadableError|in use|track start failed/i.test(msg)) {
            setError(
              "Camera is busy (another app may be using it). Close FaceTime / camera apps and try again."
            );
          } else {
            setError(msg);
          }
          setShowManual(true);
        }
      }
    };
    start();
    // If we don't decode anything within 6 seconds show a nudge —
    // most "scanner not working" reports are actually just users
    // holding the sticker too close or at a shallow angle.
    const hintTimer = setTimeout(() => {
      if (!stopped) {
        setHint("Hold the code 10–15 cm from the camera, flat and well lit. If it still doesn't scan, tap 'Enter code manually'.");
        setShowManual(true);
      }
    }, 6000);
    return () => {
      stopped = true;
      clearTimeout(hintTimer);
      const s = scannerRef.current;
      scannerRef.current = null;
      if (!s) return;
      safeStop(s).finally(() => {
        try { s.clear(); } catch { /* ignore */ }
      });
    };
  }, [open, onResult]);

  if (!open) return null;

  const submitManual = (e) => {
    e?.preventDefault?.();
    const code = manual.trim();
    if (!code) return;
    onResult?.(code);
    setManual("");
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="barcode-scanner-overlay"
      className="fixed inset-0 z-[100] bg-black/85 flex flex-col"
    >
      <div className="flex items-center justify-between p-4 text-white">
        <div className="flex items-center gap-2">
          <Camera className="w-5 h-5" />
          <span className="font-display font-bold">{title}</span>
        </div>
        <button
          data-testid="barcode-scanner-close"
          onClick={onClose}
          className="rounded-full bg-white/10 hover:bg-white/20 p-2"
          aria-label="Close scanner"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center p-2 sm:p-4">
        <div
          id={REGION_ID}
          data-testid="barcode-scanner-region"
          className="w-full max-w-xl aspect-[4/3] min-h-[260px] rounded-xl overflow-hidden bg-black"
        />
      </div>

      {error ? (
        <div className="px-4 pb-2 text-amber-200 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span data-testid="barcode-scanner-error">{error}</span>
        </div>
      ) : hint ? (
        <div className="px-4 pb-2 text-amber-200 text-sm flex items-start gap-2" data-testid="barcode-scanner-hint">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{hint}</span>
        </div>
      ) : (
        <div className="px-4 pb-2 text-white/70 text-xs text-center space-y-1">
          <p>Supports Code128 stickers, QR codes and GS1 DataMatrix on supplier boxes. Hold 8–15 cm away, flat and well lit.</p>
          <p className="text-white/50">
            iPhone: if the camera doesn&apos;t open, allow access via Settings → Safari → Camera.
          </p>
        </div>
      )}

      <div className="p-4 bg-black/60 text-white">
        {!showManual ? (
          <Button
            data-testid="barcode-manual-toggle"
            onClick={() => setShowManual(true)}
            variant="outline"
            className="w-full h-12 gap-2 bg-transparent border-white/30 text-white hover:bg-white/10"
          >
            <Keyboard className="w-4 h-4" /> Can&apos;t scan? Enter code manually
          </Button>
        ) : (
          <form onSubmit={submitManual} className="flex gap-2">
            <Input
              data-testid="barcode-manual-input"
              autoFocus
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder="e.g. EQ-FRIDGE-01"
              className="h-12 bg-white text-black"
            />
            <Button
              type="submit"
              data-testid="barcode-manual-submit"
              className="h-12 bg-ochre-600 hover:bg-ochre-400 text-white"
            >
              Use code
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
